/**
 * Bundle → Career[] adapter for the scoring engine.
 * A Supabase market lookup (live or seed) overrides the four market fields;
 * when absent we fall back to the bundle's row-keyed table; when neither
 * exists the row is `unpriced` and can never appear as a Best match.
 */
import type { CareersBundle, CareerRow, DemandBand, LiveMarketLookup } from "@/data/careersBundle";
import type { Archetype, Career, Confidence, MarketSource } from "@/lib/scoring";
import { sanitizeSalaryBand } from "@/lib/salaryBands";

function coerceConfidence(v: string): Confidence {
  const s = (v ?? "").toLowerCase();
  if (s === "high" || s === "medium" || s === "low") return s;
  return "medium";
}

export function bundleToCareers(
  bundle: CareersBundle,
  live?: LiveMarketLookup,
): Career[] {
  return bundle.rows.map((r: CareerRow) => {
    const dna: Partial<Record<Archetype, number>> = {};
    for (const a of r.archetypes) {
      dna[a.name as Archetype] = a.pct / 100;
    }

    let salaryLo: number | null = null;
    let salaryHi: number | null = null;
    let demand2040: string | null = null;
    let automationExposure: number | null = null;
    let market: MarketSource = "unpriced";
    let marketStale = false;
    let observations: number | null = null;

    const hit = live?.(r.ciId);
    if (hit) {
      salaryLo = hit.salaryLoLpa;
      salaryHi = hit.salaryHiLpa;
      demand2040 = hit.demand2040 as string | null;
      automationExposure = hit.automationExposure;
      // A stale non-seed row is used and flagged, not discarded.
      market = hit.source === "seed" ? "seed" : "live";
      marketStale = Boolean(hit.stale);
      observations = hit.observations ?? null;
    } else {
      const fb = bundle.fallbackMarket[r.rowIndex];
      if (fb) {
        salaryLo = fb[0];
        salaryHi = fb[1];
        demand2040 = fb[2] as DemandBand;
        automationExposure = fb[3];
        market = "fallback";
      }
    }


    // Zero is a salary; absence is not. An unpriced row must carry null, never
    // 0, or the UI renders "₹0–0 LPA" and the role looks free. The same guard
    // rejects a band that can't be true for the seniority of the role — a
    // Director on ₹1.5 LPA is a data fault, not a cheap Director, and scoring
    // pay against it would quietly punish or reward the wrong careers.
    {
      const clean = sanitizeSalaryBand(salaryLo, salaryHi, r.level);
      salaryLo = clean.lo;
      salaryHi = clean.hi;
    }

    return {
      ciId: r.ciId,
      title: r.name,
      family: r.family,
      cluster: r.cluster,
      level: r.level,
      entryRequirements: r.entryRequirement ?? "",
      confidence: coerceConfidence(r.confidence),
      fields: r.fields as Record<string, string | null>,
      dna,
      salaryLo, salaryHi, demand2040, automationExposure, market,
      marketStale, observations,
    } satisfies Career;
  });
}
